import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CakeIcon from '@mui/icons-material/Cake';
import CategoryIcon from '@mui/icons-material/Category';
import CloseIcon from '@mui/icons-material/Close';
import Co2RoundedIcon from '@mui/icons-material/Co2Rounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import StraightenIcon from '@mui/icons-material/Straighten';
import {
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import { Suspense } from 'react';
import { Link } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import DateRangeSelector from '../../../components/time-range-selector/DateRangeSelector';
import InfoTooltipIcon from '@/components/icons/InfoTooltipIcon';
import { formatNumber } from '@/utils/formatters';
import OccupancyDaysCard from '@/features/portfolio-analysis/components/datagrid/OccupancyDaysCard';
import { useGetMatrikelMetadataByIdQuery } from '@/api/energy-analysis/portfolioAnalysisApiSlice';
import { useSelector } from 'react-redux';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import MeterDisplayName from '@/features/portfolio-analysis/components/MeterDisplayName';

const HeaderDisplay = ({ label, description }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        mb: 2,
      }}
    >
      <Typography variant="body1" color="textSecondary" fontWeight="bold">
        {label}
      </Typography>
      <InfoTooltipIcon title={description} />
    </Box>
  );
};

const ValueDisplay = ({ label, value, icon, unit }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {icon}
      <Box sx={{ ml: 1 }}>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
        <Typography variant="h6" color="textPrimary">
          {value} {unit}
        </Typography>
      </Box>
    </Box>
  );
};

const getMatrikelMetadataKey = (matrikelMetadata, key) => {
  if (!matrikelMetadata) return null;
  if (matrikelMetadata?.OverwriteFlag) {
    return matrikelMetadata?.[`Overwrite${key}`] ?? matrikelMetadata?.[key];
  } else {
    return matrikelMetadata?.[key];
  }
};

const MeterDrawer = ({
  open,
  onClose,
  meter,
  meterId,
  meterType,
  Chart,
  chartProps,
  setSelectedMatrikel,
  setSelectedRowSettings,
  children,
  selectedChart,
  setSelectedChart,
  selectedDateRange,
  setSelectedDateRange,
  downloadCsv,
  analysisType,
}) => {
  const { t } = useTranslation(`portfolio-analysis_${analysisType}`);
  const account = useSelector(getSelectedAccount);

  const { data: matrikelMetadata } = useGetMatrikelMetadataByIdQuery(
    {
      recalc_account_id: account?.AccountId,
      matrikel_key: meter?.matrikel_key,
    },
    { skip: !account?.AccountId || !meter?.matrikel_key },
  );
  const isSolarPanel = meter?.type_of_mp === 'PV' && analysisType === 'power';
  const charts = (() => {
    if (isSolarPanel) {
      return [{ chartName: 'consumption' }, { chartName: 'yearOnYear' }];
    }
    if (analysisType === 'power') {
      return [
        { chartName: 'consumption' },
        { chartName: 'yearOnYear' },
        { chartName: 'durationAnalysis' },
        { chartName: 'hourAnalysis' },
      ];
    }
    return [
      { chartName: 'consumption' },
      { chartName: 'yearOnYear' },
      { chartName: 'hourAnalysis' },
    ];
  })();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          height: '100%',
          maxWidth: {
            lg: '60vw',
            sm: '80vw',
            xs: '100vw',
          },
        },
      }}
    >
      <Stack
        sx={{
          width: '100%',
          padding: 3,
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          {meterType == 'matrikel' ? (
            <Typography variant="h5" gutterBottom>
              {t('drawer.title.matrikel')}: {meter.main_address}
            </Typography>
          ) : (
            <Box
              display={'flex'}
              alignItems="center"
              gap={1}
              flexDirection={'row'}
              width={'100%'}
            >
              <Typography variant="h5">
                {isSolarPanel
                  ? t('drawer.title.solar_panel')
                  : t('drawer.title.meter')}
                :
              </Typography>
              <MeterDisplayName
                recalcAccountId={account?.AccountId}
                meterId={meterId}
                analysisType={analysisType}
                displayName={meter?.DisplayName || meter.address}
                address={meter?.address}
              />
            </Box>
          )}

          <IconButton
            size="small"
            onClick={onClose}
            sx={{ position: 'absolute', right: 0, borderRadius: 1, ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Grid container spacing={2}>
          {isSolarPanel && (
            <>
              <Grid item xs={12} lg>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <HeaderDisplay
                      label={t('drawer.cards.pv_net_production_year.header')}
                      description={t(
                        'drawer.cards.pv_net_production_year.description',
                      )}
                    />
                    <ValueDisplay
                      value={formatNumber(meter.pv_net_production_year)}
                      icon={<BoltRoundedIcon sx={{ mr: 1 }} />}
                      unit={t('columns.pv_net_production_year.label')}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} lg>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <HeaderDisplay
                      label={t('drawer.cards.pv_supplied_to_grid_year.header')}
                      description={t(
                        'drawer.cards.pv_supplied_to_grid_year.description',
                      )}
                    />
                    <ValueDisplay
                      value={formatNumber(meter.pv_supplied_to_grid_year)}
                      icon={<BoltRoundedIcon sx={{ mr: 1 }} />}
                      unit={t('columns.pv_supplied_to_grid_year.label')}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} lg>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <HeaderDisplay
                      label={t('drawer.cards.pv_co2e_savings_year.header')}
                      description={t(
                        'drawer.cards.pv_co2e_savings_year.description',
                      )}
                    />
                    <ValueDisplay
                      value={formatNumber(meter.pv_co2e_savings_year)}
                      icon={<Co2RoundedIcon sx={{ mr: 1 }} />}
                      unit={t('columns.pv_co2e_savings_year.label')}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
          {meterType != 'matrikel' && !isSolarPanel && (
            <Grid item xs={12} lg>
              <Stack spacing={2} sx={{ height: '100%' }}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <HeaderDisplay
                      label={t('drawer.cards.consumptionData.header')}
                      description={t(
                        'drawer.cards.consumptionData.description',
                        {
                          location_code: meter.location_code,
                        },
                      )}
                    />
                    <ValueDisplay
                      label={t('columns.total_consumption_year.header')}
                      value={formatNumber(meter.total_consumption_year)}
                      icon={<BoltRoundedIcon sx={{ mr: 1 }} />}
                      unit={t('columns.total_consumption_year.label')}
                    />
                  </CardContent>
                </Card>

                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <HeaderDisplay
                      label={t('drawer.cards.co2Data.header')}
                      description={t('drawer.cards.co2Data.description', {
                        location_code: meter.location_code,
                      })}
                    />
                    <ValueDisplay
                      label={t('columns.total_co2e_year.header')}
                      value={formatNumber(meter.total_co2e_year)}
                      icon={<Co2RoundedIcon sx={{ mr: 1 }} />}
                      unit={t('columns.total_co2e_year.label')}
                    />
                  </CardContent>
                </Card>
              </Stack>
            </Grid>
          )}

          {meterType != 'meter' && !isSolarPanel && (
            <Grid item xs={12} lg={true}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                  >
                    <HeaderDisplay
                      label={t('drawer.cards.buildingData.header')}
                      description={t('drawer.cards.buildingData.description')}
                    />
                    <div onClick={(e) => e.stopPropagation()}>
                      <Tooltip
                        title={t('drawer.cards.buildingData.bbrLinkTooltip')}
                      >
                        <Link
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                          }}
                          to={`https://kort.bbr.dk/?bfe=${meter?.BFENummer}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <IconButton sx={{ borderRadius: 1 }}>
                            <OpenInNewRoundedIcon />
                          </IconButton>
                        </Link>
                      </Tooltip>
                    </div>
                  </Box>

                  <ValueDisplay
                    label={t('columns.AverageBuildingYear.header')}
                    value={getMatrikelMetadataKey(
                      matrikelMetadata,
                      'AverageBuildingYear',
                    )?.toFixed(0)}
                    icon={<CakeIcon sx={{ mr: 1 }} />}
                    unit={t('columns.AverageBuildingYear.label')}
                  />

                  <ValueDisplay
                    label={t('columns.SumBuildingArea.header')}
                    value={getMatrikelMetadataKey(
                      matrikelMetadata,
                      'SumBuildingArea',
                    )?.toFixed(0)}
                    icon={<StraightenIcon sx={{ mr: 1 }} />}
                    unit={t('columns.SumBuildingArea.label')}
                  />

                  <ValueDisplay
                    label={t('columns.ExtractedHeatInstallation.header')}
                    value={getMatrikelMetadataKey(
                      matrikelMetadata,
                      'ExtractedHeatInstallation',
                    )}
                    icon={<LocalFireDepartmentIcon sx={{ mr: 1 }} />}
                    unit={t('columns.ExtractedHeatInstallation.label')}
                  />

                  <ValueDisplay
                    label={t('columns.ExtractedBuildingUse.header')}
                    value={getMatrikelMetadataKey(
                      matrikelMetadata,
                      'ExtractedBuildingUse',
                    )}
                    icon={<CategoryIcon sx={{ mr: 1 }} />}
                    unit={t('columns.ExtractedBuildingUse.label')}
                  />
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <Tooltip
                    title={t('drawer.cards.buildingData.editButtonTooltip')}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setSelectedMatrikel(meter)}
                    >
                      <EditRoundedIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          )}

          {meterType != 'matrikel' && !isSolarPanel && (
            <Grid item xs={12} lg>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <HeaderDisplay
                    label={t('drawer.cards.activityPatterns.header')}
                    description={t('drawer.cards.activityPatterns.description')}
                  />
                  <OccupancyDaysCard
                    daysOfWeek={
                      meter?.ManualOccupancy ??
                      meter?.CalculatedOccupancy ??
                      meter?.DefaultOccupancy
                    }
                  />
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <Tooltip
                    title={t('drawer.cards.activityPatterns.editButtonTooltip')}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setSelectedRowSettings(meter)}
                    >
                      <EditRoundedIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          )}

          {meterType != 'matrikel' && (
            <Grid item lg={12}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <HeaderDisplay
                    label={t('drawer.cards.graphData.header')}
                    description={t('drawer.cards.graphData.description')}
                  />

                  <Box
                    display={'flex'}
                    minHeight={'65px'}
                    flexDirection={'row'}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <ToggleButtonGroup
                      value={selectedChart}
                      exclusive
                      onChange={(e, newChart) => {
                        if (newChart !== null) {
                          setSelectedChart(newChart);
                        }
                      }}
                      sx={{ mr: 2 }}
                    >
                      {charts.map((chart, index) => (
                        <ToggleButton key={index} value={chart.chartName}>
                          {t(
                            `drawer.cards.graphData.charts.${chart.chartName}.name`,
                          )}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    {selectedChart !== 'yearOnYear' && (
                      <Box display="flex" flexDirection="row">
                        <Box
                          sx={{
                            display: 'flex',
                            alignContent: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            padding: 1,
                          }}
                        >
                          <Tooltip
                            title={t(
                              'drawer.cards.graphData.exportButtonTooltip',
                            )}
                          >
                            <IconButton size="large" onClick={downloadCsv}>
                              <FileDownloadIcon fontSize="medium" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box my={'auto'}>
                          <DateRangeSelector
                            key={selectedDateRange.end}
                            setTimeRange={setSelectedDateRange}
                            timeRange={selectedDateRange}
                            setDuration={() => {}}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {selectedChart && (
                    <Box>
                      <Suspense fallback={<CircularProgress />}>
                        <Chart {...chartProps} />
                      </Suspense>
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end' }}></CardActions>
              </Card>
            </Grid>
          )}
        </Grid>
        {children}
      </Stack>
    </Drawer>
  );
};

export default MeterDrawer;
